import { FC, PropsWithChildren } from "react";
import styled from "styled-components";

import {
  DateHelpers,
  formatDate,
  IStyledAttributes,
  isValidDateTime,
  NO_VALUE_DISPLAY,
  nowTDateISO,
  useTimezone,
  VEHICLE_LICENSE_EXPIRY_LIMIT,
} from "@ryde/shared";
import { greenMui, redMui, rydeColors } from "@ryde/theme";

import { IRenderColumnTemplate } from "../../dynamic-forms";

interface IStatusStyledProps extends PropsWithChildren {
  diffInDays?: number | null;
  licenseExpiryLimitation: number;
}

const TextWithIndicatorStyled = styled.div.attrs<IStyledAttributes>({
  "data-e2e": "text-with-indicator",
})`
  display: flex;
  align-items: center;
  font-weight: 500;
  gap: 10px;
  color: ${rydeColors.black};
`;

export const DiffStatusStyled = styled.span.attrs<IStyledAttributes>({
  "data-e2e": "diff-status-styled",
})<IStatusStyledProps>`
  display: inline-block;
  border-radius: 50%;
  height: 9px;
  width: 9px;
  background-color: ${props => getBgColor(props)};
`;

export const getBgColor = ({
  diffInDays,
  licenseExpiryLimitation,
}: IStatusStyledProps): string => {
  if (!diffInDays) {
    return rydeColors.lightGrey;
  }

  if (diffInDays < 0) {
    return redMui._700;
  }

  if (diffInDays > licenseExpiryLimitation) {
    return greenMui._700;
  } else {
    return rydeColors.yellow;
  }
};

type ITextWithIndicatorCellProps = {
  children: string;
} & Pick<IRenderColumnTemplate, "dateFormat">;

export const TextWithIndicatorCell: FC<ITextWithIndicatorCellProps> = ({
  children,
  dateFormat,
}) => {
  const isValidDate = isValidDateTime(children);
  const { timezone } = useTimezone();
  const diffInDays = isValidDate
    ? DateHelpers.differenceBetweenDates(
        children as TDateISO,
        nowTDateISO(timezone),
      )
    : null;

  return (
    <TextWithIndicatorStyled>
      <DiffStatusStyled
        diffInDays={diffInDays}
        licenseExpiryLimitation={VEHICLE_LICENSE_EXPIRY_LIMIT}
      />
      {isValidDate
        ? formatDate({ dateISO: children as TDateISO, dateFormat, timezone })
        : NO_VALUE_DISPLAY}
    </TextWithIndicatorStyled>
  );
};
